import { useQuery } from '@tanstack/react-query';
import { Search, Star } from 'lucide-react';
import { useState } from 'react';

import { Input } from '@/components/ui/input';
import { getFavourites } from '@/services/api.client';

interface PickedContact {
  name: string;
  phone: string;
}

interface Props {
  propertyId: string;
  unitId: string;
  onSelect: (contact: PickedContact) => void;
}

export function ContactPicker({ propertyId, unitId, onSelect }: Props) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const { data: favourites } = useQuery({
    queryKey: ['favourites', propertyId, unitId],
    queryFn: () => getFavourites(propertyId, unitId),
  });

  const list = (favourites ?? []).map((f) => ({
    id: f.id,
    name: f.visitorName,
    phone: f.visitorPhone,
  }));

  const filtered = searchTerm
    ? list.filter(
        (c) =>
          c.name.toLowerCase().includes(searchTerm.toLowerCase()) || c.phone.includes(searchTerm),
      )
    : list.slice(0, 8);

  if (list.length === 0) return null;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-[var(--text-secondary)]">
          Quick select from Favourites
        </p>
        <button
          type="button"
          onClick={() => setSearchOpen(!searchOpen)}
          className="flex items-center gap-1 text-xs text-[var(--text-accent)] hover:underline"
        >
          {searchOpen ? 'Close' : 'Search'}
        </button>
      </div>

      {searchOpen && (
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 size-3.5 text-muted-foreground" />
          <Input
            placeholder="Search favourites…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8 h-8 text-sm"
          />
        </div>
      )}

      <div className="flex flex-wrap gap-1.5">
        {filtered.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => onSelect({ name: c.name, phone: c.phone })}
            className="inline-flex items-center gap-1.5 rounded-full border border-[var(--bg-surface-raised)] bg-[var(--bg-surface)] px-3 py-1 text-xs font-medium text-[var(--text-primary)] hover:bg-[var(--bg-surface-raised)] transition-colors"
          >
            <Star className="size-3 text-[var(--text-accent)]" />
            <span className="truncate max-w-[120px]">{c.name}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
